<template>
  <div class="sub-form-store" v-if="!hidden" :data-sub-form-id="subFormId">
    <sub-form-view-container
      :list-head="listHead"
      :path="path"
      :read-only="computedReadOnly"
      :isReadView="isReadView"
      @edit-row-click="showModalEdit"
      @remove-row-click="onRemoveRowClick"
      :key="path"
      :data-subform-layout-id="form.id"
    />
    <el-button
      v-if="showAddButton"
      class="subform-add-btn m-2"
      :class="{ 'ms-0': !showWithHeader, required: isRequired }"
      :data-test-add-subform-row="name"
      @click.stop="onAddRowClick"
      type="default"
      icon="el-icon-plus"
      :disabled="computedReadOnly"
    >
      {{ $i18n.t('Add item') }}
    </el-button>

    <modal id="subform-row-edit" :title="subformModalTitle" :is-open="isModalOpen" :show-close="false" size="90%">
      <sub-form-row
        :list-head="listHead"
        :is-list-view-modal="true"
        :path="`${path}/${openRowId}`"
        :read-only="readOnly"
        :key="path"
      />
      <div class="d-flex justify-content-end" slot="title-buttons">
        <el-button
          class="subform-save-btn m-2"
          :data-test-save-subform-row="name"
          @click.stop="onSaveRowClick"
          type="primary"
          icon="el-icon-check"
        >
          {{ $i18n.t('save_sub_form_row') }}
        </el-button>
        <el-button
          class="subform-cancel-btn m-2"
          :data-test-cancel-subform-row="name"
          @click.stop="onDiscardRowClick"
          type="default"
        >
          {{ $i18n.t('discard_sub_form_row') }}
        </el-button>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from '../modal/Modal'
import SubFormViewContainer from './wrapper/SubFormViewContainer.vue'
import SubFormGridView from './wrapper/SubFormGridView.vue'
import SubFormDetailView from './wrapper/SubFormDetailView.vue'
import { toSubFormField2 } from '../../form'
import { cloneDeep, get, find, isEqual, isEmpty, compact, isUndefined, sortBy } from 'lodash'
import EventBus from '../../utils/event-bus'
import { mapActions } from 'vuex'
import formstore from '../../utils/mixin-formstore'
import SubFormRow from './SubFormRow.vue'
import { uuidv4 } from '../query-builder/helpers/helpers'
import confirmation from '../../utils/mixin-confirmation'
import { FIELD_PROPS, FIELD_REQUIRED_LEVELS, GET_DATA_FIELD_PROPS } from '../../consts'

/**
 * @class SubFormStore
 *
 * @description This component renders and handles the state of a subform
 */
export default {
  name: 'SubFormStore',
  mixins: [formstore, confirmation],
  components: {
    Modal,
    SubFormRow,
    SubFormGridView,
    SubFormDetailView,
    SubFormViewContainer,
  },
  props: {
    path: {
      type: String,
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cloneForm: {},
      idx: -1,
      subFormTemplateData: null,
      isModalOpen: false,
      openRowId: 0,
      rowCache: {},
      discardAction: 'cache',
    }
  },
  computed: {
    form() {
      return this.field.subForm
    },
    isSubformFull() {
      return this.maxRecCount && this.recordCount >= this.maxRecCount
    },
    recordCount() {
      return this.rows.length
    },
    subformModalTitle() {
      return this.$i18n.interpolate(this.$i18n.t('add_row_for_subform_${label}'), { label: this.field.label })
    },
    subformView() {
      return this.$get('subformView')
    },
    subformValue() {
      return this.field.subformValue || this.field.value || this.value || []
    },
    submitRoot() {
      return this.$get('submitRoot')
    },
    extendable() {
      return get(this.field, 'subRecExtendable', false) && !this.isSubformFull
    },
    showAddButton() {
      if (this.isReadView) {
        return false
      } else {
        return !this.readOnly && this.extendable
      }
    },
    showWithHeader() {
      return !get(this.field, 'subformPure', false)
    },
    isReadView() {
      return this.$get('isReadView')
    },
    maxRecCount: {
      get() {
        return this.field.maxRecCount || 0
      },
      set(value) {
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'maxRecCount',
          value,
        })
      },
    },
    minRecCount: {
      get() {
        return get(this.field, 'subMinNumberOfRec', 0)
      },
      set(value) {
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'subMinNumberOfRec',
          value,
        })
      },
    },
    builderData() {
      return this.field.subFormBuilderData
    },
    subFormId() {
      return this.builderData && this.builderData.id
    },
    rows: {
      get() {
        return this.form.rows || []
      },
      set(value) {
        if (!this.$store.hasModule(this.$storeNamespace)) {
          return
        }
        this.$commit('FORM_SET_DIRTY', { isDirty: true })
        this.$commit('FORM_UPDATE_SUBFORM_BY_PATH', {
          path: this.path,
          data: value,
        })
      },
    },
    validationName() {
      return `${this.$storeNamespace}-${this.name}`
    },
    subFormValidations() {
      const validations = {}
      if (this.isRequired) validations.required = this.isRequired
      if (this.minRecCount) validations.min_value = this.minRecCount
      if (this.maxRecCount) validations.max_value = this.maxRecCount
      return validations
    },
    isRequired() {
      const errorLevel = get(this.field, [FIELD_PROPS.VALIDATIONS, FIELD_PROPS.REQUIRED], FIELD_REQUIRED_LEVELS.NONE)
      return (
        [FIELD_REQUIRED_LEVELS.WARNING, FIELD_REQUIRED_LEVELS.ERROR].includes(errorLevel) ||
        (this.minRecCount && Number(this.minRecCount) > 0)
      )
    },
    computedReadOnly() {
      return this.readOnly || this.groupReadOnly
    },
    isListView() {
      return this.subformView === 'List'
    },
    rowProto() {
      return { items: cloneDeep(get(this.form, ['data', 'ITEMS', this.id, 'PROTO'], [])) }
    },
    listHead() {
      if (!this.cloneForm.heads) return []
      let headers = []
      this.cloneForm.heads.forEach((header, index) => {
        let existingHeader
        let showInList = true

        for (const row of this.rows) {
          if (header.hasOwnProperty('labelExp')) {
            const item = row.items.find((item) => item.name === header.name)
            if (item.label) {
              header.label = item.label
            }
          }
          existingHeader = find(row.items, { label: header.label })
          if (existingHeader && !existingHeader.hidden && !find(headers, { label: existingHeader.label })) {
            if (this.isListView && get(this, 'builderData.showInList.length', 0) > 0) {
              showInList = this.builderData.showInList.find((el) => el === existingHeader.name)
            }
            if (showInList) {
              const required = this.isHeaderRequired(index)
              headers.push({ label: existingHeader.label, name: existingHeader.name, required })
            }
          }
        }
      })
      return compact(headers)
    },
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setUrl', 'setUrls', 'setSubForm', 'setSubFormTempData']),
    get,
    showModalEdit({ index, discardAction }) {
      this.openRowId = index
      this.discardAction = discardAction
      this.rowCache = cloneDeep(this.rows[index])
      this.isModalOpen = true
    },
    onSaveRowClick() {
      this.isModalOpen = false
    },
    onDiscardRowClick() {
      this.isModalOpen = false
      if (this.discardAction === 'cache') {
        this.$dispatch('setSubFormRowData', {
          path: this.path,
          rowIndex: this.openRowId,
          data: this.rowCache,
        })
      } else {
        this.$dispatch('removeSubFormRow', {
          path: this.path,
          rowIndex: this.openRowId,
        })
      }
    },
    /**
     *
     * @description Új sor hozzáadása
     * @returns {Promise<void>}
     */
    onAddRowClick() {
      const subFormRowId = this.rows.length || 0
      const rowUid = uuidv4()

      const row = {
        items: this.rowProto.items.map((item) => {
          return Object.assign({}, item, item.inputOptions, toSubFormField2(item))
        }),
        rowId: subFormRowId,
        rowUid,
      }

      this.$dispatch('addSubFormRow', { path: this.path, row })
      this.$dispatch('resolveSubFormExpressions', {
        subForm: true,
        formId: this.subFormId,
        rowId: subFormRowId,
        path: this.path,
        formName: this.formName,
      })
      if (this.isListView) {
        this.showModalEdit({
          index: this.rows.length - 1,
          discardAction: 'remove',
        })
      }
    },
    async onRemoveRowClick(rowIndex) {
      if (!this.computedReadOnly) {
        const confirmed = await this._confirm('delete_selected_items_confirm')
        if (confirmed) {
          this.$dispatch('removeSubFormRow', { path: this.path, rowIndex })
          this.$dispatch('triggerExpressionResolver', {
            field: this.field,
            prop: 'value',
          })
        }
      }
    },
    onValidate() {
      this.$nextTick(() => {
        if (
          !find(get(this.$validator, '_base.fields.items', []), {
            name: this.validationName,
          })
        )
          return
        const storeValue = this.rows.length === 0 ? '' : this.rows.length
        this.$validator.validate(this.validationName, storeValue).then(() => {
          EventBus.$emit('errors-changed', this.validationName)
        })
      })
    },
    /**
     * @description frissiti a nem lathato elemeket
     * @returns {Promise<void>}
     */
    async refreshHiddenProperties() {
      for (const page of Object.values(this.builderData.pages)) {
        for (const group of Object.values(page.groups)) {
          if (!group.hiddenExp) {
            continue
          } else {
            //@TODO kell ez egyaltalan
            //if (!await ExpressionResolver.resolve(group.hiddenExp.exp, this.$store[this.$storeNamespace])) continue
          }

          for (const item of Object.values(group.items)) {
            if (this.rowProto.items) {
              const rowProtoItemInGroup = this.rowProto.items.find((i) => item.id === i.id)
              rowProtoItemInGroup.hidden = true
            }
          }
        }
      }
    },
    /**
     *
     * @description Visszaadja, hogy az adott elem kötelező-e
     * @param index
     * @returns {boolean}
     */
    isHeaderRequired(index) {
      const errorLevel = get(
        this.cloneForm,
        [FIELD_PROPS.ITEMS, index, FIELD_PROPS.VALIDATIONS, FIELD_PROPS.REQUIRED],
        FIELD_REQUIRED_LEVELS.NONE
      )
      return [FIELD_REQUIRED_LEVELS.WARNING, FIELD_REQUIRED_LEVELS.ERROR].includes(errorLevel)
    },
    needConversion(subFormValue) {
      return Array.isArray(subFormValue) && !get(subFormValue, '0.items')
    },
    getRowsFromExpressionValue(subFormValue) {
      const rows = []
      let rowUid
      subFormValue.forEach((row) => {
        rowUid = uuidv4()
        const tmpRow = {
          items: this.rowProto.items.map((item) =>
            Object.assign({}, item, item.inputOptions, toSubFormField2(item), {
              value: get(row, [item.id, GET_DATA_FIELD_PROPS.VALUE], get(row, item.id, '')),
              hidden: get(row, [item.id, GET_DATA_FIELD_PROPS.HIDDEN], item.hidden),
              readOnly: get(row, [item.id, GET_DATA_FIELD_PROPS.READ_ONLY], item.readOnly),
              label: get(row, [item.id, GET_DATA_FIELD_PROPS.LABEL], item.label),
            })
          ),
          rowUid,
        }
        rows.push(tmpRow)
      })

      return rows
    },
    isValidValue(subFormValue) {
      return !!get(subFormValue, '0.items', false)
    },
  },
  mounted() {
    this.cloneForm = cloneDeep(this.form)
    this.$validator.attach({
      name: this.validationName,
      rules: this.subFormValidations,
    })
    EventBus.$on('subFormStoreValidate', this.onValidate)
  },
  beforeDestroy() {
    this.$validator.detach(this.validationName)
    EventBus.$off('subFormStoreValidate')
    EventBus.$off('subFormPopupSave')
    EventBus.$off('popupClose')
  },
  watch: {
    subFormValidations: {
      handler() {
        this.$validator.detach(this.validationName)
        this.$validator.attach({
          name: this.validationName,
          rules: this.subFormValidations,
        })
      },
      deep: true,
    },
    minRecCount: {
      handler() {
        this.validations = this.subFormValidations
        this.$validator.detach(this.validationName)
        this.$validator.attach({
          name: this.validationName,
          rules: this.subFormValidations,
        })

        if (this.rows.length === 0 && this.minRecCount > 0 && !this.$get('idMainTable')) {
          for (let i = 0; i < this.minRecCount; ++i) {
            this.onAddRowClick()
          }
          this.onValidate()
        }
      },
    },
    maxRecCount: {
      handler() {
        this.validations = this.subFormValidations
        this.$validator.detach(this.validationName)
        this.$validator.attach({
          name: this.validationName,
          rules: this.subFormValidations,
        })
      },
    },
    builderData: {
      async handler(newVal) {
        if (typeof newVal !== 'undefined') await this.refreshHiddenProperties()
      },
    },
    subformValue: {
      handler(newVal, oldVal) {
        if (isEqual(newVal, oldVal) || isUndefined(newVal) || isEqual(this.rows, newVal)) return

        this.rows = this.needConversion(newVal)
          ? this.getRowsFromExpressionValue(newVal)
          : this.isValidValue(newVal)
            ? newVal
            : this.rows

        this.$dispatch('resolveSubFormExpressions', {
          subForm: true,
          formId: this.subFormId,
          path: this.path,
        })
      },
      deep: true,
    },
  },
}
</script>

<style scoped lang="scss">
.sub-form-store {
  padding: 5px;
}

button.required:after {
  content: '*';
  color: $color-not-set;
}
</style>
